// src/lib/AuthContext.jsx
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, setTokens, clearTokens, getTokens } from './api.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // No "who am I" endpoint call needed here — the case queue's first
    // request will 401 -> refresh -> succeed if the session is still good.
    // We just check whether a token exists at all to decide the first render.
    const { accessToken } = getTokens();
    setLoading(false);
    if (!accessToken) setStaff(null);
    const cached = sessionStorage.getItem('sauti_staff');
    if (cached) setStaff(JSON.parse(cached));
  }, []);

  const login = useCallback(async (username, password) => {
    const body = await api('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) });
    if (body.mfaRequired) return { mfaRequired: true, mfaToken: body.mfaToken };
    setTokens(body);
    setStaff(body.staff);
    sessionStorage.setItem('sauti_staff', JSON.stringify(body.staff));
    return { mfaRequired: false };
  }, []);

  const verifyMfa = useCallback(async (mfaToken, code) => {
    const body = await api('/auth/mfa/verify', { method: 'POST', body: JSON.stringify({ mfaToken, code }) });
    setTokens(body);
    setStaff(body.staff);
    sessionStorage.setItem('sauti_staff', JSON.stringify(body.staff));
  }, []);

  const logout = useCallback(async () => {
    const { refreshToken } = getTokens();
    try { await api('/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken }) }); } catch { /* best-effort */ }
    clearTokens();
    sessionStorage.removeItem('sauti_staff');
    setStaff(null);
  }, []);

  return (
    <AuthContext.Provider value={{ staff, loading, login, verifyMfa, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
